import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ArrowLeft, FileText, Printer, Scale } from 'lucide-react';
import { Button, Chip } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { formatCPF, formatName } from '@/lib/format';
import { useCliente } from '@/modules/clientes/api';
import { decodeClienteId } from '@/modules/clientes/cliente-id';
import {
  buildOnusRows,
  OnusGravamesTable,
  type OnusRow,
} from '@/modules/clientes/components/ClienteRelatoriosTab';
import { emitirSolicitacaoBaixaPdf } from '@/modules/clientes/components/OnusGravamesEditor';

export const Route = createFileRoute('/_authed/clientes_/$id_/relatorio/onus-gravames')({
  component: OnusGravamesRoute,
});

const RELATORIO_NOME = 'Relatório de Ônus e Gravames';

type Filtro = 'todos' | 'vigente' | 'vencido_sem_baixa';

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #onus-print-area, #onus-print-area * { visibility: visible !important; }
  #onus-print-area {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    padding: 12px 16px !important;
  }
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  @page { size: A4 landscape; margin: 8mm; }

  #onus-print-area table {
    font-size: 8pt !important;
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
  }
  #onus-print-area th,
  #onus-print-area td {
    padding: 5px 6px !important;
    vertical-align: top !important;
    word-wrap: break-word !important;
    overflow-wrap: anywhere !important;
    white-space: normal !important;
    max-width: none !important;
    min-width: 0 !important;
  }
  /* coluna Instrumento/Observações expandida em PDF */
  #onus-print-area td:last-child,
  #onus-print-area th:last-child { width: 26% !important; }
  #onus-print-area th:nth-child(2),
  #onus-print-area td:nth-child(2) { width: 9% !important; }
  #onus-print-area th:nth-child(3),
  #onus-print-area td:nth-child(3) { width: 8% !important; }
  #onus-print-area th:nth-child(4),
  #onus-print-area td:nth-child(4) { width: 11% !important; }
  #onus-print-area th:nth-child(5),
  #onus-print-area td:nth-child(5) { width: 12% !important; }
  #onus-print-area th:nth-child(6),
  #onus-print-area td:nth-child(6) { width: 11% !important; }
  #onus-print-area th:nth-child(7),
  #onus-print-area td:nth-child(7) { width: 13% !important; }
  #onus-print-area th:nth-child(8),
  #onus-print-area td:nth-child(8) { width: 10% !important; }

  #onus-print-area tr { page-break-inside: avoid !important; }
  #onus-print-area .chip {
    font-size: 7pt !important;
    padding: 1px 5px !important;
    white-space: normal !important;
  }
  /* Tipo e Status: chip não quebra (Arrendado / Necessita de baixa) */
  #onus-print-area td:nth-child(2) .chip,
  #onus-print-area td:nth-child(7) .chip {
    white-space: nowrap !important;
  }
  /* Tipo, Matrícula, Status, Vencimento: dados centralizados */
  #onus-print-area th:nth-child(2),
  #onus-print-area td:nth-child(2),
  #onus-print-area th:nth-child(3),
  #onus-print-area td:nth-child(3),
  #onus-print-area th:nth-child(7),
  #onus-print-area td:nth-child(7),
  #onus-print-area th:nth-child(8),
  #onus-print-area td:nth-child(8) {
    text-align: center !important;
  }
}
`;

function OnusGravamesRoute() {
  const { id } = Route.useParams();
  const cpf = decodeClienteId(id);
  const navigate = useNavigate();
  const { data, isLoading, error } = useCliente(cpf);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!data?.nome) return;
    const previousTitle = document.title;
    document.title = `${RELATORIO_NOME} - ${formatName(data.nome)}`;
    return () => {
      document.title = previousTitle;
    };
  }, [data?.nome]);

  const back = (
    <Button
      variant="ghost"
      onClick={() => {
        if (window.history.length > 1) window.history.back();
        else void navigate({ to: '/clientes/$id', params: { id }, search: { relatorio: undefined, tab: 'relatorios', from_pipe: undefined, from_grupo: undefined } });
      }}
    >
      <ArrowLeft size={14} strokeWidth={1.5} />
      Voltar ao cliente
    </Button>
  );

  if (isLoading) {
    return (
      <PageShell eyebrow="Relatórios" title="Ônus e Gravames" back={back}>
        <p className="text-sm text-text-muted">Carregando…</p>
      </PageShell>
    );
  }

  if (error || !data) {
    return (
      <PageShell eyebrow="Relatórios" title="Ônus e Gravames" back={back}>
        <p className="text-sm text-danger">
          {error instanceof ApiError ? error.message : 'Falha ao carregar cliente.'}
        </p>
      </PageShell>
    );
  }

  const rows = buildOnusRows(data);
  const ativos = rows.filter((r) => r.status === 'vigente').length;
  const pendentes = rows.filter((r) => r.status === 'vencido_sem_baixa').length;
  const visible = filtro === 'todos' ? rows : rows.filter((r) => r.status === filtro);
  const selectedRows = rows.filter((row) => selectedIds.has(row.id));
  const selectionMatricula = selectedRows[0]?.matricula ?? null;

  function toggleSelection(row: OnusRow) {
    if (!row.matricula) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  }

  function emitirSolicitacaoSelecionada() {
    const cliente = data;
    const first = selectedRows[0];
    if (!cliente || !first || !selectionMatricula) return;
    const atos = Array.from(
      new Map(selectedRows.map((row) => [row.item.id, row.item])).values(),
    );
    emitirSolicitacaoBaixaPdf({
      atos,
      context: {
        nomeDono: first.proprietario_nome ?? cliente.nome,
        cpfDono: first.proprietario_cpf ?? cliente.cpf,
        matricula: selectionMatricula,
        cartorio: first.cartorio,
        municipio: first.municipio ?? cliente.municipio,
        uf: first.uf ?? cliente.uf,
        imovelNome: first.imovel,
        proprietarioNome: first.proprietario_nome ?? cliente.nome,
      },
    });
  }

  const municipioUf = [data.municipio ? formatName(data.municipio) : null, data.uf]
    .filter(Boolean)
    .join(' / ');

  return (
    <PageShell eyebrow="Relatórios" title="Ônus e Gravames" back={back}>
      <style>{PRINT_CSS}</style>
      <div id="onus-print-area">
        <Stack gap="md">
          <h1
            className="print-only text-text font-extrabold"
            style={{
              display: 'none',
              fontSize: '14pt',
              letterSpacing: '-0.02em',
              margin: '0 0 6px 0',
            }}
          >
            Relatório de Ônus e Gravames
          </h1>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-2">
              <span
                className="uppercase font-semibold text-text-muted"
                style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
              >
                Cliente:
              </span>
              <span className="text-text font-semibold" style={{ fontSize: '0.95rem' }}>
                {formatName(data.nome)}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="uppercase font-semibold text-text-muted"
                style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
              >
                CPF:
              </span>
              <span className="text-text" style={{ fontSize: '0.9rem' }}>
                {formatCPF(data.cpf)}
              </span>
              {municipioUf && (
                <Chip tone="info" size="sm">
                  {municipioUf}
                </Chip>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setFiltro('todos')}
              className={`chip cursor-pointer no-print ${filtro === 'todos' ? '!bg-[var(--accent-soft)] !text-accent !border-none' : 'chip-muted'}`}
              style={{ fontSize: '0.78rem' }}
            >
              Todos · {rows.length}
            </button>
            <button
              type="button"
              onClick={() => setFiltro('vigente')}
              className={`chip cursor-pointer no-print ${filtro === 'vigente' ? 'chip-ok' : 'chip-muted'}`}
              style={{ fontSize: '0.78rem' }}
            >
              Ativos · {ativos}
            </button>
            <button
              type="button"
              onClick={() => setFiltro('vencido_sem_baixa')}
              className={`chip cursor-pointer no-print ${filtro === 'vencido_sem_baixa' ? 'chip-warn' : 'chip-muted'}`}
              style={{ fontSize: '0.78rem' }}
            >
              Pendentes de Baixa · {pendentes}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="chip chip-danger cursor-pointer no-print inline-flex items-center gap-1"
              style={{ fontSize: '0.78rem' }}
              aria-label="Gerar PDF"
            >
              <FileText size={11} strokeWidth={1.8} />
              PDF
            </button>
            <Button
              variant="primary"
              size="sm"
              className="no-print"
              disabled={selectedRows.length === 0}
              onClick={emitirSolicitacaoSelecionada}
            >
              <Printer size={13} strokeWidth={1.8} />
              Emitir solicitação de baixa
              {selectedRows.length > 0 ? ` · ${selectedRows.length}` : ''}
            </Button>
            {selectedRows.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="chip chip-muted cursor-pointer no-print"
                style={{ fontSize: '0.78rem' }}
              >
                Limpar seleção · matrícula {selectionMatricula}
              </button>
            )}
          </div>

          {visible.length === 0 ? (
            <div className="empty-state py-10">
              <div className="empty-state-icon">
                <Scale size={22} strokeWidth={1.5} />
              </div>
              <p className="empty-state-title">Nenhum ônus</p>
              <p className="empty-state-desc">
                {filtro === 'todos'
                  ? 'Os imóveis deste produtor estão livres de ônus cadastrados.'
                  : 'Nada neste filtro.'}
              </p>
            </div>
          ) : (
            <OnusGravamesTable
              rows={visible}
              selectedIds={selectedIds}
              selectionMatricula={selectionMatricula}
              onToggleSelection={toggleSelection}
            />
          )}
        </Stack>
      </div>
    </PageShell>
  );
}
